import { Form, Input, Select } from 'antd';
import Fuse from 'fuse.js';
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator';
import React, { useEffect, useMemo, useState } from 'react';

import { useCountries } from '@aims/shared/shared/use-countries';
import { useCountryRegions } from '@aims/shared/shared/use-country-regions';

const countrySearchOptions = {
  includeScore: true,
  keys: ['shortCode', 'name'],
};

const countryRegionsSearchOptions = {
  includeScore: true,
  keys: ['shortCode', 'name'],
};

function SingleAddressFormItem({ loading, form, num }) {
  const {
    loading: countriesLoading,
    error: countriesError,
    countries,
  } = useCountries();
  const [countrySearch, setCountrySearch] = useState('');

  const countryOptions = useMemo(() => {
    if (countrySearch) {
      // console.log('countrySearch', countrySearch);
      const fuse = new Fuse(countries, countrySearchOptions);
      const listItems = fuse
        .search(countrySearch)
        .sort((a, b) => a.score - b.score)
        .map((i) => i.item);
      // console.log('listItems', listItems, fuse.search(countrySearch));
      return listItems;
    } else {
      return countries || [];
    }
  }, [countrySearch, countries]);

  const [selectedCountryCode, setSelectedCountryCode] = useState(null);
  const country = Form.useWatch('country', form);

  const {
    loading: countryRegionsLoading,
    error: countryRegionsError,
    countryRegions,
  } = useCountryRegions(country);
  const [countryRegionsSearch, setCountryRegionsSearch] = useState('');

  const countryRegionsOptions = useMemo(() => {
    if (countryRegionsSearch) {
      const fuse = new Fuse(countryRegions, countryRegionsSearchOptions);
      const listItems = fuse
        .search(countryRegionsSearch)
        .sort((a, b) => a.score - b.score)
        .map((i) => i.item);
      return listItems;
    } else {
      return countryRegions;
    }
  }, [countryRegionsSearch, countryRegions]);
  return (
    <>
      <Form.Item
        label="Label"
        name="label"
        initialValue={`Address ${num}`}
        rules={[
          { required: true, message: 'Please enter a label for this address' },
        ]}
        extra="e.g., My house, office ..."
      >
        <Input autoComplete="chrome-off" disabled={loading} />
      </Form.Item>
      <Form.Item label="Address Line 1" name="address1">
        <Input autoComplete="chrome-off" disabled={loading} />
      </Form.Item>
      <Form.Item label="Address Line 2" name="address2" rules={[]}>
        <Input autoComplete="chrome-off" disabled={loading} />
      </Form.Item>
      <Form.Item style={{ marginBottom: 0 }}>
        <Form.Item
          label="Country"
          name="country"
          rules={[{ required: true, message: 'Please select a country' }]}
          style={{
            display: 'inline-block',
            width: 'calc(50% - 8px)',
          }}
        >
          <Select
            showSearch
            onSearch={(value) => {
              setCountrySearch(value);
            }}
            onSelect={(value) => {
              setCountrySearch('');
            }}
            optionFilterProp="children"
            autoComplete="chrome-off"
            disabled={loading}
            loading={countriesLoading}
            onChange={(value) => {
              setSelectedCountryCode(value);
              form.validateFields();
            }}
          >
            {countryOptions.map((_country) => (
              <Select.Option
                key={_country.shortCode}
                value={_country.shortCode}
              >
                {_country.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="State / Region"
          name="state"
          style={{
            display: 'inline-block',
            width: 'calc(50% - 8px)',
            marginLeft: 16,
          }}
        >
          <Select
            showSearch
            onSearch={(value) => {
              setCountryRegionsSearch(value);
            }}
            onSelect={(value) => {
              setCountryRegionsSearch('');
            }}
            optionFilterProp="children"
            autoComplete="chrome-off"
            disabled={loading}
            loading={countryRegionsLoading}
          >
            {countryRegionsOptions.map((state) => (
              <Select.Option key={state.shortCode} value={state.shortCode}>
                {state.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form.Item>
      <Form.Item label="City" name="city">
        <Input autoComplete="chrome-off" disabled={loading} />
      </Form.Item>
      <Form.Item
        label="Zip Code / Postal Code"
        name="zipCode"
        rules={[
          {
            validator: (_, value) => {
              if (
                value &&
                postcodeValidatorExistsForCountry(country) &&
                !postcodeValidator(value, country)
              ) {
                return Promise.reject('Please enter a valid zip code.');
              } else {
                return Promise.resolve(value);
              }
            },
          },
        ]}
        style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
      >
        <Input disabled={loading} />
      </Form.Item>
    </>
  );
}

export default SingleAddressFormItem;
